import { useState } from 'react'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { CommitNaTela } from '../../types/repositorios'
import { ArquivosDoCommit } from './ArquivosDoCommit'
import { CelulaDaSolicitacao, CHIP } from './CelulaDaSolicitacao'
import { EtiquetaDeSetor } from './EtiquetaDeSetor'

const INTEIRO = new Intl.NumberFormat('pt-BR')

/** Quantas colunas a tabela tem — a sublinha dos arquivos ocupa todas. */
export const COLUNAS_DA_LISTA = 6

/**
 * Uma linha da lista de commits, com a sublinha dos arquivos que abre sob demanda.
 *
 * Os arquivos não vêm abertos porque um commit do e-Cidade toca dezenas de caminhos e a lista de
 * 50 commits viraria uma parede de texto; o botão diz quantos são antes de abrir.
 *
 * @param props o commit
 * @returns a linha (e a sublinha, quando aberta)
 */
export function LinhaDeCommit({ commit }: { commit: CommitNaTela }) {
  const t = TEXTOS.repositorios.commits
  const [arquivosAbertos, setArquivosAbertos] = useState(false)
  const nomeCurto = commit.repositorio.nomeCompleto.split('/').pop() ?? commit.repositorio.nomeCompleto
  const quantosArquivos = commit.arquivosAlterados ?? commit.arquivos?.length ?? 0

  return (
    <>
      <tr className="border-b border-slate-100 align-top last:border-0">
        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
          {dataHora(commit.commitEm)}
          <p className="font-mono text-xs text-slate-400">{commit.sha.slice(0, 7)}</p>
        </td>
        <td className="px-3 py-2">
          <p className="text-sm text-slate-900">{commit.autor.nome}</p>
          <p className="mt-1 flex flex-wrap gap-1">
            <EtiquetaDeSetor setor={commit.autor.setor} />
            {commit.autor.equipeInterna && (
              <span className={`${CHIP} bg-slate-100 text-slate-700 ring-slate-200`}>{TEXTOS.repositorios.equipeInterna.chip}</span>
            )}
          </p>
        </td>
        <td className="px-3 py-2 text-xs">
          <a
            href={commit.repositorio.url}
            target="_blank"
            rel="noreferrer"
            className="text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
            title={commit.repositorio.nomeCompleto}
          >
            {nomeCurto}
          </a>
        </td>
        <td className="px-3 py-2">
          <a
            href={commit.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-slate-900 underline-offset-2 hover:underline"
            title={t.verNoGithub}
          >
            {commit.titulo}
          </a>
          <p className="mt-1 flex flex-wrap gap-1">
            {commit.integracao && <span className={`${CHIP} bg-slate-100 text-slate-700 ring-slate-200`}>{t.integracao}</span>}
            {commit.codigoExterno && <span className={`${CHIP} bg-slate-50 text-slate-600 ring-slate-200`}>{commit.codigoExterno}</span>}
            {commit.chamadoNumero && (
              <span className={`${CHIP} bg-sky-50 text-sky-900 ring-sky-200`}>{t.chamado(commit.chamadoNumero)}</span>
            )}
          </p>
        </td>
        <td className="px-3 py-2 text-sm">
          <CelulaDaSolicitacao commit={commit} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-slate-600">
          {commit.adicoes == null ? (
            '—'
          ) : (
            <>
              <span className="text-green-700">+{INTEIRO.format(commit.adicoes)}</span>
              {' / '}
              <span className="text-red-700">−{INTEIRO.format(commit.remocoes ?? 0)}</span>
            </>
          )}
          {quantosArquivos > 0 && (
            <p>
              <button
                type="button"
                aria-expanded={arquivosAbertos}
                onClick={() => setArquivosAbertos((aberto) => !aberto)}
                className="text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
              >
                {arquivosAbertos ? t.esconderArquivos : t.verArquivos(quantosArquivos)}
              </button>
            </p>
          )}
        </td>
      </tr>
      {arquivosAbertos && (
        <tr className="border-b border-slate-100 last:border-0">
          <td colSpan={COLUNAS_DA_LISTA} className="px-3 pb-3">
            <ArquivosDoCommit commit={commit} />
          </td>
        </tr>
      )}
    </>
  )
}
